import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";

export function useCatalogModel(catalogId, onModel) {
  const receive = useRef(onModel);
  receive.current = onModel;
  const [state, setState] = useState({ id: null, loading: false, error: "" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!catalogId) return;
    const controller = new AbortController();
    setState({ id: catalogId, loading: true, error: "" });
    (async () => {
      try {
        const { data: item } = await api.get(`/prints/catalog/${encodeURIComponent(catalogId)}`, { signal: controller.signal });
        const ext = (item.file_ext || "").toLowerCase();
        if (!item.file_ref || ![".stl", ".obj"].includes(ext)) {
          throw new Error("This catalog entry has no previewable model attached yet.");
        }
        const { data: blob } = await api.get(`/prints/upload/${encodeURIComponent(item.file_ref)}`, {
          responseType: "blob", signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const original = item.file_name || item.name || "Catalog model";
        const name = original.toLowerCase().endsWith(ext) ? original : `${original}${ext}`;
        receive.current({
          file: new File([blob], name, { type: blob.type || "application/octet-stream" }),
          uploaded: { file_ref: item.file_ref, file_ext: ext, file_name: name, file_size_bytes: blob.size },
          scale: Number(item.default_scale) > 0 ? Number(item.default_scale) : 1,
        });
        setState({ id: catalogId, loading: false, error: "" });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({ id: catalogId, loading: false,
          error: error.response ? apiError(error, "This catalog model could not be loaded.") : error.message });
      }
    })();
    return () => controller.abort();
  }, [catalogId, attempt]);

  return { loading: !!catalogId && (state.id !== catalogId || state.loading),
    error: state.id === catalogId ? state.error : "", retry: () => setAttempt(n => n + 1) };
}